import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, name?: string) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  resendEmailConfirmation: (email: string) => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if Supabase is configured
    if (!isSupabaseConfigured()) {
      console.warn('Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your environment variables.');
      setLoading(false);
      return;
    }

    let mounted = true;
    let timeoutId: NodeJS.Timeout | null = null;

    // Check if there's a hash in the URL (token from email confirmation or password reset)
    // If so, let onAuthStateChange handle it instead of calling getSession() immediately
    // This ensures Supabase has time to process the hash token
    const hash = window.location.hash;
    const hasAuthToken = hash && (hash.includes('access_token') || hash.includes('type=recovery'));

    // Get initial session only if there's no auth token in the URL
    // When there's a token, onAuthStateChange will fire after Supabase processes it
    if (!hasAuthToken) {
      supabase.auth.getSession()
        .then(({ data: { session }, error }) => {
          if (!mounted) return;

          if (error) {
            console.error('Error getting session:', error);
          } else {
            setSession(session);
            setUser(session?.user ?? null);
          }
          setLoading(false);
        })
        .catch((error) => {
          if (!mounted) return;
          console.error('Error getting session:', error);
          setLoading(false);
        });
    } else {
      // When there's a token in the URL, wait for onAuthStateChange to fire
      // Set a timeout to stop loading if onAuthStateChange doesn't fire within 5 seconds
      timeoutId = setTimeout(() => {
        if (mounted) {
          setLoading(false);
        }
      }, 5000);
    }

    // Listen for auth changes (this fires when hash tokens are processed)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        // Clear timeout if onAuthStateChange fires
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      }
    });

    return () => {
      mounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, name?: string) => {
    // Sign up with email confirmation enabled
    // Users can access dashboard immediately if "Allow unconfirmed users to sign in" is enabled in Supabase
    // The banner will show if email is not confirmed
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name: name || '',
        },
        // Only set emailRedirectTo for password reset flow, not for signup
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    // Check if user was created successfully, even if email sending failed
    // If user exists, signup succeeded - any error is likely just about email sending
    const isEmailError = error?.message?.toLowerCase().includes('email') ||
      error?.message?.toLowerCase().includes('confirmation') ||
      error?.message?.toLowerCase().includes('sending');

    // If user was created, proceed with signup (email errors are non-blocking)
    if (data?.user) {
      // Check if this is a repeated signup (email already exists)
      // Supabase returns a user with empty identities array for repeated signups
      // to prevent email enumeration attacks
      if (data.user.identities && data.user.identities.length === 0) {
        return {
          error: {
            message: "This email is already registered. Please sign in instead, or use a different email.",
            name: "EmailAlreadyInUse",
            status: 400
          } as AuthError
        };
      }
      // User was created successfully, proceed even if email error occurred

      // Store user metadata in database
      try {
        await supabase.from('profiles').upsert({
          id: data.user.id,
          email: data.user.email,
          name: name || '',
          created_at: new Date().toISOString(),
        });
      } catch (dbError) {
        console.error('Failed to create user profile:', dbError);
        // Don't fail signup if profile creation fails
      }

      // Email confirmation is handled automatically by Supabase
      // Custom welcome emails should be sent via Edge Functions or server-side APIs to avoid CORS issues

      // Only set session if email is confirmed (email_confirmed_at is not null)
      // If email is not confirmed, user needs to verify email before accessing the app
      if (data.session && data.user.email_confirmed_at) {
        setSession(data.session);
        setUser(data.session.user);
      }
      // If no session or email not confirmed, user will need to confirm email first
      // The signup is still successful - user was created, just needs email confirmation

      // User was created successfully - return success (error is just about email sending)
      // Log warning if there was an email-related error
      if (error && isEmailError) {
        console.warn('User created but confirmation email failed to send:', error.message);
      }
      return { error: null };
    }

    // Return error only if user was not created
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    // If sign in was successful, check if email is confirmed
    if (!error && data?.user) {
      if (!data.user.email_confirmed_at) {
        // Email is not confirmed, sign out the user and return error
        await supabase.auth.signOut();
        return {
          error: {
            message: "Please verify your email before signing in. Check your inbox for the confirmation link.",
            name: "EmailNotConfirmed",
            status: 400
          } as AuthError
        };
      }
    }

    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback`,
    });

    return { error };
  };

  const resendEmailConfirmation = async (email: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    return { error };
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    resendEmailConfirmation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

